import React, { useState } from "react";
import { DollarOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Modal,
  notification,
  Radio,
  Row,
  Statistic,
  Table,
  Tag,
} from "antd";
import { useParams } from "react-router-dom";
import { useGetLoanQuery, useGetLoanScheduleQuery } from "store/api";
import { useLoanRepaymentMutation } from "store/api/requests/loan/loanRepaymentApiSlice";

const SingleLoanApplication = () => {
  const { id } = useParams();
  const [modalVisible, setModalVisible] = React.useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<any>({});

  const { data: loan, isLoading } = useGetLoanQuery(id);
  const {
    data: loanSchedule,
    isFetching: isLoadingSchedule,
    refetch,
  } = useGetLoanScheduleQuery(id);

  type FieldType = {
    accountNumber?: string;
    password?: string;
    accountType?: string;
    documentName?: string;
    documentDescription?: string;
    amount?: string;
    transactionDate?: string;
    transactionId?: string;
    transactionType?: string;
    paymentMethod?: string;
  };

  const [loanRepayment, { isLoading: isRepaying }] = useLoanRepaymentMutation();
  const [maxLength, setMaxLength] = useState(11);

  const scheduleColumns = [
    {
      title: "Payment Number",
      dataIndex: "paymentNumber",
      key: "paymentNumber",
    },
    {
      title: "Payment Date",
      dataIndex: "paymentDate",
      key: "paymentDate",
    },
    {
      title: "Principal",
      dataIndex: "principal",
      key: "principal",
      render: (text: string) => (
        <span>
          {Number(text).toLocaleString("en", {
            style: "currency",
            currency: "UGX",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
        </span>
      ),
    },
    {
      title: "Interest",
      dataIndex: "interest",
      key: "interest",
    },
    {
      title: "Payment Amount",
      dataIndex: "paymentAmount",
      key: "paymentAmount",
      render: (text: string) => (
        <span>
          {Number(text).toLocaleString("en", {
            style: "currency",
            currency: "UGX",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
        </span>
      ),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      render: (text: string) => (
        <span>
          {Number(text).toLocaleString("en", {
            style: "currency",
            currency: "UGX",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
        </span>
      ),
    },
    {
      title: "Repayment Balance",
      dataIndex: "repaymentBalance",
      key: "repaymentBalance",
      render: (text: string) => (
        <span>
          {Number(text).toLocaleString("en", {
            style: "currency",
            currency: "UGX",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          })}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text: any) => (
        <Tag color={text === "paid" ? "green" : "red"} className="capitalize">
          {text}
        </Tag>
      ),
    },
    {
      title: "Actions",
      render: (_text: any, record: any) => {
        return (
          <Button
            disabled={
              loan?.loanStatus === "Pending" ||
              loan?.isDisbursed === "false" ||
              record.status === "paid" ||
              record.status === "pending-approval"
            }
            icon={<DollarOutlined />}
            type="link"
            onClick={() => {
              setModalVisible(true);
              setSelectedSchedule(record);
            }}
          >
            Repay
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      <Card className="mb-4 text-center" loading={isLoading}>
        <h4 className="text-md font-semibold mb-3 capitalize">
          {loan?.loanTypeName}
        </h4>
        <Tag color={loan?.loanStatus === "Pending" ? "blue" : "green"}>
          Status: {loan?.loanStatus}
        </Tag>
        <Tag color={loan?.isDisbursed === "false" ? "" : "yellow"}>
          Disbursed: {loan?.isDisbursed === "false" ? "No" : "Yes"}
        </Tag>
      </Card>
      <Row gutter={16}>
        <Col sm={24} md={8}>
          <Card loading={isLoading}>
            <Statistic
              title="Loan Amount"
              value={loan?.loanAmount}
              suffix="UGX"
            />
          </Card>
        </Col>
        <Col sm={24} md={8}>
          <Card loading={isLoading}>
            <Statistic
              title="Loan Term"
              value={loan?.loanTerm}
              suffix={"Months"}
            />
          </Card>
        </Col>
        <Col sm={24} md={8}>
          {" "}
          <Card loading={isLoading}>
            <Statistic
              title="Interest Rate"
              value={loan?.interestRate}
              suffix="%"
            />
          </Card>
        </Col>
      </Row>
      <Table
        columns={scheduleColumns}
        dataSource={(loanSchedule?.data ?? [])
          .slice()
          .sort((a: any, b: any) => a.paymentNumber - b.paymentNumber)}
        pagination={{ pageSize: 20 }}
        style={{ marginTop: 20 }}
        loading={isLoadingSchedule}
      />
      <Modal
        centered
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setSelectedSchedule({});
        }}
        title="Repay Loan"
        footer={null}
      >
        <Form
          layout="vertical"
          initialValues={{
            amount: selectedSchedule.repaymentBalance,
          }}
          onFinish={async (values) => {
            if (values.amount > selectedSchedule.repaymentBalance) {
              notification.error({
                message: "Repayment Failed",
                description: "Amount should be less repayment balance",
              });
              return;
            }
            try {
              await loanRepayment({
                loanScheduleId: selectedSchedule.id,
                amountRepaid: values.amount,
                loanId: id,
                transactionId: values.transactionId,
                transactionDate: values.transactionDate,
                paymentMethod: values.paymentMethod,
              }).unwrap();

              notification.success({
                message: "Repayment Success",
                description: "Amount repaid successfully!",
              });
              setModalVisible(false);
              refetch();
              setSelectedSchedule({});
            } catch (error: any) {
              notification.error({
                message: "Error",
                description: error?.data?.message || "Failed to repay",
              });
            }
          }}
        >
          <Form.Item
            label="Amount To Pay"
            name="amount"
            rules={[
              {
                required: true,
                message: "Please input the amount to repay",
              },
            ]}
          >
            <Input placeholder="Enter Amount" />
          </Form.Item>

          <Form.Item<FieldType>
            label="Payment Method"
            layout="vertical"
            name="paymentMethod"
            rules={[
              { required: true, message: "Please select a payment method" },
            ]}
            initialValue="mtn"
            className=""
          >
            <Radio.Group
              options={[
                { label: "MTN Mobile Money", value: "mtn" },
                { label: "Airtel Money", value: "airtel" },
                { label: "Bank", value: "bank" },
              ]}
              optionType="button"
              buttonStyle="solid"
              className="w-full"
            />
          </Form.Item>

          <Form.Item<FieldType>
            label="Transaction ID"
            layout="vertical"
            name="transactionId"
            dependencies={["paymentMethod"]}
            rules={[
              { required: true, message: "Please input transaction Id" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const method = getFieldValue("paymentMethod");

                  if (!value) return Promise.resolve();

                  if (!/^\d+$/.test(value)) {
                    return Promise.reject(
                      new Error("Transaction ID must contain only numbers"),
                    );
                  }

                  if (method === "mtn" && value.length !== 11) {
                    setMaxLength(11);
                    return Promise.reject(
                      new Error("MTN Transaction ID must be 11 digits"),
                    );
                  }
                  if (
                    (method === "airtel" || method === "bank") &&
                    value.length !== 12
                  ) {
                    setMaxLength(12);
                    return Promise.reject(
                      new Error(
                        `${method === "airtel" ? "Airtel" : "Bank"} Transaction ID must be 12 digits`,
                      ),
                    );
                  }

                  return Promise.resolve();
                },
              }),
            ]}
            className=""
          >
            <Input
              className="bg-slate-100 h-10"
              inputMode="numeric"
              maxLength={maxLength}
              onChange={(e) => {
                e.target.value = e.target.value.replace(/\D/g, "");
              }}
            />
          </Form.Item>

          <Form.Item<FieldType>
            label="Transaction Date"
            layout="vertical"
            name="transactionDate"
            rules={[{ required: true, message: "Please provide the date" }]}
            className="mb-12 sm:mb-14 "
          >
            <DatePicker className="w-full h-10" />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            className="w-full h-10"
            loading={isRepaying}
            disabled={isRepaying}
          >
            Repay Loan
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default SingleLoanApplication;
